import { PrecisionType } from "@/src/components/selector/date-precision-selector"
import { getDateRangeItems, DateRangeType } from "@/src/constants/date-range-items"
import { differenceInDays, format, isSameDay, startOfHour, subDays, subHours, subMonths } from "date-fns"
import { useSearchParams } from "next/navigation"
import { DateRange } from "react-day-picker"
import TimeAgo from "javascript-time-ago"
import en from "javascript-time-ago/locale/en"
import { parseISO } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"

const useInitialDateRange = (): DateRangeType => {
  const dateRangeItems = getDateRangeItems()
  const searchParams = useSearchParams()
  const startingDate = searchParams?.get("starting-date")
  const endingDate = searchParams?.get("ending-date")
  // don't have time stamps - change the date range to 'last week'
  if (!startingDate || !endingDate) {
    return DateRangeType.lastWeek
  }
  for (let i = 0; i < dateRangeItems.length; i++) {
    const element = dateRangeItems[i]
    if (
      isSameDay(element.date[0], new Date(startingDate)) &&
      isSameDay(element.date[1], new Date(endingDate))
    ) {
      return element.value
    }
  }
  return DateRangeType.custom
}

const getStartingMonth = (_dateRangeDetail?: DateRange) => {
  return "the last period"
}

const useInitialDateDetails = () => {
  const searchParams = useSearchParams()
  const startingDate = searchParams?.get("starting-date")
  const endingDate = searchParams?.get("ending-date")
  const today = startOfHour(new Date())
  const defaultRange = {
    from: new Date(subDays(today, 7)),
    to: today,
  }
  // 'custom' but don't have time stamps - change the date range to 'last week'
  if (!startingDate || !endingDate) {
    return defaultRange
  }
  // 'custom' with both time stamps - fall back to the default range if either param ismalformed
  const from = new Date(startingDate)
  const to = new Date(endingDate)
  if (isNaN(from.getTime()) || isNaN(to.getTime())) {
    return defaultRange
  }
  return { from, to }
}

function getFormattedDate(date: string) {
  if (!date) {
    return ""
  }
  TimeAgo.addLocale(en)
  const timeAgo = new TimeAgo("en-US")
  return timeAgo.format(parseISO(date + "Z"))
}

function getOrdinalSuffix(day: number) {
  if (day > 3 && day < 21) return "th" // 11th, 12th, 13th, etc.
  switch (day % 10) {
    case 1:
      return "st"
    case 2:
      return "nd"
    case 3:
      return "rd"
    default:
      return "th"
  }
}

function formatDateWithOrdinal(date: Date, timezone: "UTC" | "LOCAL" = "LOCAL") {
  if (!date || isNaN(date.getTime())) {
    return ""
  }
  if (timezone === "UTC") {
    return formatInTimeZone(date, "UTC", "yyyy MMM do")
  }
  const day = date.getDate()
  const month = date.toLocaleString("en-US", { month: "short" })
  const year = date.getFullYear()

  return `${year} ${month} ${day}${getOrdinalSuffix(day)}`
}

function formateTimeWithOrdinal(date: Date, timezone: "UTC" | "LOCAL" = "LOCAL") {
  if (!date || isNaN(date.getTime())) {
    return ""
  }
  if (timezone === "UTC") {
    return formatInTimeZone(date, "UTC", "yyyy MMM do HH:mm")
  }
  const hour = String(date.getHours()).padStart(2, "0")
  const minute = String(date.getMinutes()).padStart(2, "0")
  const day = date.getDate()
  const month = date.toLocaleString("en-US", { month: "short" })
  const year = date.getFullYear()

  return `${year} ${month} ${day}${getOrdinalSuffix(day)} ${hour}:${minute}`
}

const getTimeDescription = (
  dateRangeDetail?: DateRange,
  showTime?: boolean,
  timezone: "UTC" | "LOCAL" = "LOCAL",
) => {
  if (!dateRangeDetail) {
    return ""
  }
  if (showTime) {
    return `${formateTimeWithOrdinal(dateRangeDetail?.from as Date, timezone) || ""} - ${
      formateTimeWithOrdinal(dateRangeDetail?.to as Date, timezone) || ""
    }`
  }
  return `${formatDateWithOrdinal(dateRangeDetail?.from as Date, timezone) || ""} - ${
    formatDateWithOrdinal(dateRangeDetail?.to as Date, timezone) || ""
  }`
}

const getLocalTimeFromUTC = (UTC: string) => {
  return new Date(UTC).toISOString()
}

const minute = 60 * 1000
const hour = 60 * minute
const day = 24 * hour
const month = 30 * day
const year = 365 * day

export function getRelativeLastSeenTime(time: string): string {
  const needsFormatting = !/Z$/.test(time) || /\.\d{6,}/.test(time)
  const formattedTime = needsFormatting ? time.replace(/(\.\d{3})\d+/, "$1").replace(/(?<!Z)$/, "Z") : time

  const now = Date.now()
  const timestamp = new Date(formattedTime).getTime()
  const diff = Math.max(0, now - timestamp)

  if (diff < hour) {
    const mins = Math.floor(diff / minute)
    if (mins === 0) {
      return "<1min"
    }
    return `${mins}min`
  } else if (diff < day) {
    const hours = Math.floor(diff / hour)
    return `${hours}h`
  } else if (diff < month) {
    const days = Math.floor(diff / day)
    return `${days}d`
  } else if (diff < year) {
    const months = Math.floor(diff / month)
    return `${months}m`
  } else if (diff > year) {
    const years = Math.floor(diff / year)
    return `${years}y`
  }
  return "recently"
}

export type ChartPrecisionMode = "default" | "shrink" | "coarse"

export function getChartPrecision(dateRangeDetail?: DateRange, mode: ChartPrecisionMode = "default") {
  if (dateRangeDetail?.from && dateRangeDetail?.to) {
    const diffDays = differenceInDays(dateRangeDetail.to, dateRangeDetail.from)

    if (mode === "coarse") {
      return diffDays >= 3 ? PrecisionType.day : PrecisionType.hour
    }

    if (diffDays >= 90) {
      return PrecisionType.day
    }
    if (diffDays >= 30) {
      return PrecisionType.fourHours
    }
    if (mode === "shrink" && diffDays < 1) {
      return PrecisionType.minute
    }
    if (mode === "shrink" && diffDays <= 2) {
      return PrecisionType.fiveMinutes
    }
    if (mode === "shrink" && diffDays <= 7) {
      return PrecisionType.hour
    }
  }
  return PrecisionType.hour
}

function roundTheDateToTodayUTC0000(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
}

function roundTheDateToNextUTC0000(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, 0))
}

/** Convert a Date to UTC 00:00:00.000 of its local calendar date */
export function startOfUTCDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

/** Convert a Date to UTC 23:59:59.999 of its local calendar date */
export function endOfUTCDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999))
}

/** UTC start of week (Monday). Reads UTC date components from the input. */
export function startOfUTCWeek(date: Date): Date {
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth()
  const d = date.getUTCDate()
  const dow = new Date(Date.UTC(y, m, d)).getUTCDay() // 0=Sun
  const mondayOffset = dow === 0 ? -6 : 1 - dow
  return new Date(Date.UTC(y, m, d + mondayOffset))
}

/** UTC end of week (Sunday 23:59:59.999). Reads UTC date components from the input. */
export function endOfUTCWeek(date: Date): Date {
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth()
  const d = date.getUTCDate()
  const dow = new Date(Date.UTC(y, m, d)).getUTCDay()
  const sundayOffset = dow === 0 ? 0 : 7 - dow
  return new Date(Date.UTC(y, m, d + sundayOffset, 23, 59, 59, 999))
}

/** UTC start of month (1st day 00:00). Reads UTC date components from the input. */
export function startOfUTCMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

/** UTC end of month (last day 23:59:59.999). Reads UTC date components from the input. */
export function endOfUTCMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 23, 59, 59, 999))
}

/** Create a local Date whose date components match the UTC date of the input.
 *  Used so that react-day-picker (which reads local date) highlights the correct UTC day. */
export function utcDateToLocalDisplay(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export function utcSubDays(date: Date, days: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - days))
}

export function startOfUTCDayInSeconds(date: Date): number {
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / 1000)
}

export function endOfUTCDayInSeconds(date: Date): number {
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1) / 1000) - 1
}

export function getAccurateRelativeTime(
  time: string,
  showAbsoluteTime?: boolean,
  timezone: "UTC" | "LOCAL" = "LOCAL",
): string {
  if (!time) {
    return ""
  }
  const needsFormatting = !/Z$/.test(time) || /\.\d{6,}/.test(time)
  const formattedTime = needsFormatting ? time.replace(/(\.\d{3})\d+/, "$1").replace(/(?<!Z)$/, "Z") : time

  const now = Date.now()
  const timestamp = new Date(formattedTime).getTime()
  const diff = Math.max(0, now - timestamp)

  if (diff < minute) {
    return "just now"
  }

  const years = Math.floor(diff / year)
  const months = Math.floor((diff % year) / month)
  const days = Math.floor((diff % month) / day)
  const hours = Math.floor((diff % day) / hour)
  const mins = Math.floor((diff % hour) / minute)

  if (showAbsoluteTime && days > 1) {
    return `in ${formatDateWithOrdinal(new Date(formattedTime), timezone)}`
  }

  const parts: string[] = []
  if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`)
  if (months > 0 && parts.length < 2) parts.push(`${months} month${months > 1 ? "s" : ""}`)
  if (days > 0 && parts.length < 2) parts.push(`${days} day${days > 1 ? "s" : ""}`)
  if (hours > 0 && parts.length < 2) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`)
  if (mins > 0 && parts.length < 2) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`)

  return parts.join(", ") + " ago"
}

const getRealtimeDateRange = (type: DateRangeType): DateRange => {
  switch (type) {
    case DateRangeType.lastHour:
      return { from: subHours(new Date(), 1), to: new Date() }
    case DateRangeType.last3Hours:
      return { from: subHours(new Date(), 3), to: new Date() }
    case DateRangeType.last24Hours:
      return { from: subDays(new Date(), 1), to: new Date() }
    case DateRangeType.last3days:
      return { from: subDays(new Date(), 3), to: new Date() }
    case DateRangeType.lastWeek:
      return { from: subDays(new Date(), 7), to: new Date() }
    case DateRangeType.lastMonth:
      return { from: subMonths(new Date(), 1), to: new Date() }
    default:
      return { from: new Date(), to: new Date() }
  }
}

const now = new Date()

export function getLocalTimezoneString(): string {
  const offset = -now.getTimezoneOffset() / 60
  const sign = offset >= 0 ? "+" : "-"
  return `GMT${sign}${Math.abs(offset)}`
}

export const localTimezone = getLocalTimezoneString()

export function formatTimestampWithTimezone(
  date: Date | string,
  timezone: "UTC" | "LOCAL" = "LOCAL",
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date

  if (timezone === "UTC") {
    return formatInTimeZone(dateObj, "UTC", "yyyy-MM-dd HH:mm:ss") + " UTC"
  } else {
    return `${format(dateObj, "yyyy-MM-dd HH:mm:ss")} ${localTimezone}`
  }
}

export function getTimezoneLabel(timezone: "UTC" | "LOCAL" = "LOCAL"): string {
  return timezone === "UTC" ? "UTC" : localTimezone
}

export function formatChartTick(
  value: string,
  shownPrecision: string | undefined,
  timezone: "UTC" | "LOCAL" = "LOCAL",
): string {
  const date = new Date(value)
  if (timezone === "UTC") {
    return shownPrecision === "day"
      ? formatInTimeZone(date, "UTC", "MMM d")
      : formatInTimeZone(date, "UTC", "MMM d HH:mm")
  }
  return shownPrecision === "day" ? format(date, "MMM d") : format(date, "MMM d HH:mm")
}

export function formatChartTooltipLabel(
  value: string,
  timezone: "UTC" | "LOCAL" = "LOCAL",
  shownPrecision?: string,
): string {
  const date = new Date(value)
  if (shownPrecision === "day") {
    if (timezone === "UTC") {
      return formatInTimeZone(date, "UTC", "yyyy-MM-dd")
    }
    return format(date, "yyyy-MM-dd")
  }
  if (timezone === "UTC") {
    return formatInTimeZone(date, "UTC", "yyyy-MM-dd HH:mm") + " UTC"
  }
  return `${format(date, "yyyy-MM-dd HH:mm")} ${localTimezone}`
}

const readDateRangeFromParams = (from?: string, to?: string): DateRange | undefined => {
  if (!from || !to) {
    return undefined
  }
  const parsedFrom = new Date(from)
  const parsedTo = new Date(to)
  if (isNaN(parsedFrom.getTime()) || isNaN(parsedTo.getTime()) || parsedFrom >= parsedTo) {
    return undefined
  }
  return { from: parsedFrom, to: parsedTo }
}

const dateRangeToParams = (range?: DateRange) => ({
  from: range?.from?.toISOString() ?? null,
  to: range?.to?.toISOString() ?? null,
})

export {
  readDateRangeFromParams,
  dateRangeToParams,
  getStartingMonth,
  getTimeDescription,
  getLocalTimeFromUTC,
  getRealtimeDateRange,
  useInitialDateRange,
  useInitialDateDetails,
  getFormattedDate,
  roundTheDateToTodayUTC0000,
  roundTheDateToNextUTC0000,
  formateTimeWithOrdinal,
  formatDateWithOrdinal,
}
